/**
 * Single source of truth for documentation structure.
 *
 * Used by BOTH the public /docs page and the in-app DocsPanel so the two
 * surfaces can never drift. Sections are .mdx files in ./sections, loaded
 * lazily (each becomes its own chunk, fixtures included).
 *
 * `adminOnly: true` hides an item from non-admin users and from the public
 * /docs page entirely.
 */

import { lazy } from 'react';

export const CATEGORIES = [
  {
    title: 'Getting Started',
    items: [
      { id: 'overview', label: 'Overview' },
      { id: 'navigation-controls', label: 'Navigation & Controls' },
    ],
  },
  {
    title: 'Chart Types',
    items: [
      { id: 'chart-types', label: 'Time & Volume Charts' },
    ],
  },
  {
    title: 'Indicators',
    items: [
      { id: 'how-greeks-work', label: 'How Greeks Work' },
      { id: 'options-oi', label: 'Options / OI' },
      { id: 'spx-spy-overlays', label: 'Index Overlays (SPX & SPY)' },
      { id: 'volume-indicators', label: 'Volume Indicators' },
      { id: 'session-levels', label: 'Session Levels' },
      { id: 'order-flow', label: 'Order Flow' },
      { id: 'market-structure', label: 'Market Structure' },
    ],
  },
  {
    title: 'Panels',
    items: [
      { id: 'panels', label: 'Side Panels' },
    ],
  },
];

// Lazy MDX section components. A missing entry renders the "coming soon"
// placeholder, so sections can be added incrementally.
export const SECTIONS = {
  'overview': lazy(() => import('./sections/overview.mdx')),
  'navigation-controls': lazy(() => import('./sections/navigation-controls.mdx')),
  'chart-types': lazy(() => import('./sections/chart-types.mdx')),
  'how-greeks-work': lazy(() => import('./sections/how-greeks-work.mdx')),
  'options-oi': lazy(() => import('./sections/options-oi.mdx')),
  'spx-spy-overlays': lazy(() => import('./sections/spx-spy-overlays.mdx')),
  'volume-indicators': lazy(() => import('./sections/volume-indicators.mdx')),
  'session-levels': lazy(() => import('./sections/session-levels.mdx')),
  'order-flow': lazy(() => import('./sections/order-flow.mdx')),
  'market-structure': lazy(() => import('./sections/market-structure.mdx')),
  'panels': lazy(() => import('./sections/panels.mdx')),
};

export const DEFAULT_SECTION = 'overview';

/** Categories visible for a given role ('admin' shows everything). */
export function visibleCategories(role) {
  const isAdmin = role === 'admin';
  return CATEGORIES
    .filter(cat => isAdmin || !cat.adminOnly)
    .map(cat => ({
      ...cat,
      items: cat.items.filter(item => isAdmin || !item.adminOnly),
    }))
    .filter(cat => cat.items.length > 0);
}

/** Find an item's label across categories (for placeholders/titles). */
export function sectionLabel(id) {
  for (const cat of CATEGORIES) {
    const item = cat.items.find(i => i.id === id);
    if (item) return item.label;
  }
  return id;
}
